import { useEffect, useMemo, useRef, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";

import {
  useCrudEditContext,
  useDropdownSourceContext,
  usePageContext,
} from "@ryde/redux";
import {
  formTabControlsSF,
  ICustomField,
  IDropdownItem,
  IExtendableDto,
  ISelectListItem,
  ISelectListItemExt,
} from "@ryde/shared";

interface IUseInfluencedDropdownTemplate {
  formAttributes: ICustomField["formAttributes"];
  name: ICustomField["name"];
}

export const useInfluencedDropdownTemplate = ({
  formAttributes,
  name,
}: IUseInfluencedDropdownTemplate) => {
  const { setValue, watch } = useFormContext();
  const { entity } = usePageContext();
  const { getDropdownItems } = useDropdownSourceContext();
  const [, startTransition] = useTransition();
  const {
    data: { isEdit },
  } = useCrudEditContext<IExtendableDto>();
  const influencedItemsRef = useRef<
    IDropdownItem[] | ISelectListItem[] | undefined
  >([]);

  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const sourceField = currentTabControls.find(
    control => control.name === formAttributes?.influencedBy?.controlName,
  );
  const isSelect = Boolean(sourceField?.formAttributes?.selectItems);
  const isDropDown = Boolean(sourceField?.formAttributes?.dropItemsSource);

  const possibleValues: ISelectListItem["value"][] = useMemo(
    () => formAttributes?.selectItems?.map(item => item.value) ?? [],
    [formAttributes?.selectItems],
  );

  const watchedSourceField = formAttributes?.influencedBy?.controlName
    ? watch(formAttributes?.influencedBy?.controlName)
    : undefined;
  const watchedSourceFieldRef = useRef<unknown>(undefined);

  useEffect(() => {
    /*
     * Check is it initial setup for this field
     * on edit we load values for form and setting them triggers formAttributes
     * changes
     * so we want to be sure that we are watching changes of observable,
     * not watchedSourceField changed from `undefined` to observable
     *  */
    if (watchedSourceFieldRef.current === undefined) {
      watchedSourceFieldRef.current = watchedSourceField;

      if (isEdit) {
        return;
      }
    }

    if (!influencedItemsRef.current?.length) {
      return;
    }

    if (isSelect) {
      const foundItem = influencedItemsRef.current.find(
        item => item.value === watchedSourceField,
      );

      if (!foundItem) {
        return;
      }

      const newValue = foundItem.value;

      if (!possibleValues?.includes(newValue)) {
        return;
      }

      setValue(name, newValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (isDropDown) {
      const foundItem = influencedItemsRef.current?.find(
        //todo check why id and not value is used? (this is not used currently in any control)
        item => item.value === watchedSourceField,
      );

      if (!foundItem) {
        return;
      }

      const newValue = (foundItem as ISelectListItemExt)[
        String(formAttributes?.influencedBy?.valueKey)
      ];

      if (!newValue) {
        return;
      }

      if (!possibleValues?.includes(newValue as ISelectListItem["value"])) {
        setValue(name, null, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }

      setValue(name, newValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    formAttributes?.influencedBy?.valueKey,
    isDropDown,
    isEdit,
    isSelect,
    name,
    possibleValues,
    setValue,
    watchedSourceField,
  ]);

  useEffect(() => {
    const initial = async (): Promise<void> => {
      if (isSelect) {
        influencedItemsRef.current = sourceField?.formAttributes?.selectItems;
      } else if (isDropDown) {
        const sourceFieldName = sourceField?.formAttributes?.dropItemsSource;

        if (!sourceFieldName) {
          return;
        }

        influencedItemsRef.current = await getDropdownItems(sourceFieldName);
      }
    };

    startTransition(() => {
      initial();
    });
  }, [
    currentTabControls,
    formAttributes?.influencedBy?.controlName,
    getDropdownItems,
    isDropDown,
    isSelect,
    name,
    sourceField?.formAttributes?.dropItemsSource,
    sourceField?.formAttributes?.selectItems,
  ]);
};
